import React, { useState, useEffect, useContext } from "react";
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined, CloseOutlined, UploadOutlined } from "@ant-design/icons";
import { Form, Input, Select, DatePicker, Button, message, Row, Card, Col, Modal, Upload } from "antd";
import { getallBoard, getallDeveloperForTask, getallReporterLoginUser, getProjectName } from "../../utils/API";
import dayjs from "dayjs";
import { addTask } from "../../utils/API";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../Auth/AuthContext";
import { RcFile } from "antd/es/upload";
import { UploadFile } from "antd/lib";


// TipTap imports
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";


// Custom image extension with size control
const CustomImage = Image.extend({
    addAttributes() {
        return {
            width: {
                default: "150px", // Default smaller width
            },
            height: {
                default: "auto",
            },
            // Include any existing attributes from the base Image extension if needed
            // You can manually copy known attributes here if required
            src: {},
            alt: {},
            title: {},
        };
    },
});


interface Board {
    _id: string;
    name: string;
}
interface TaskFormModalProps {
    visible: boolean;
    onClose: () => void;
    onTaskCreated: () => void;
}

const { Option } = Select;

const TaskFormModal: React.FC<TaskFormModalProps> = (props) => {
    const { visible, onClose, onTaskCreated } = props;
    const [form] = Form.useForm();
    const [developers, setDevelopers] = useState<{ _id: string; username: string; }[]>([]);
    const [reporters, setReporters] = useState<{ _id: string; reporterName: string; }[]>([]);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isStatusDisabled, setIsStatusDisabled] = useState(true);
    const [boards, setBoards] = useState<Board[]>([]);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [previewImage, setPreviewImage] = useState<string>("");
    const [descValue, setDescValue] = useState("");
    const [uploadedImages, setUploadedImages] = useState<File[]>([]);
    const [ProjectName, setProjectName] = useState<any>([]);
    const [datePickerOpen, setDatePickerOpen] = useState(false);

    const navigate = useNavigate();
    const [showSubTasks, setShowSubTasks] = useState(false);
    const addSubtask = React.useRef<() => void>(() => { });
    // const [isAdmin, setIsAdmin] = useState(false);
    const { authData } = useContext(AuthContext);
    const isAdmin = authData.roleId?.roleId === 1;


    // TipTap editor instance with custom image
    const editor = useEditor({
        extensions: [
            StarterKit,
            CustomImage,
            Placeholder.configure({
                placeholder: "Enter task description 4 or upload image",
                showOnlyWhenEditable: true,
            }),
        ],
        content: descValue || "",
        editable: true,
        onTransaction({ editor }) {
            // optional: you can capture changes here
        },
    });
    const addImage = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.click();

        input.onchange = () => {
            const file = input.files?.[0];
            if (!file) return;

            setUploadedImages((prev) => [...prev, file]);

            const reader = new FileReader();
            reader.onload = () => {
                //@ts-ignore
                editor?.chain().focus().setImage({ src: reader.result as string, width: " 200px" }).run();
            };
            reader.readAsDataURL(file);
        };
    };
const handleDeadlineDateChange = (date: dayjs.Dayjs | null) => {
        try {
            // Update form value immediately
            form.setFieldValue('deadlineDate', date);
            
            // Optional: Show feedback to user
            // if (date) {
            //     message.success(`Deadline set to: ${date.format('DD-MM-YYYY HH:mm')}`, 2);
            // }
            
            // Auto-close the picker after a short delay
            setTimeout(() => {
                setDatePickerOpen(false);
            }, 300);
        } catch (error) {
            console.error('Error setting deadline date:', error);
        }
    };
    useEffect(() => {
        const fetchBoards = async () => {
            const response = await getallBoard("", 1);
            setBoards(response.data);
        };
        fetchBoards();
    }, []);

    useEffect(() => {
        const fetchDevelopers = async () => {
            try {
                setLoading(true);
                const response: any = await getallDeveloperForTask("", "");
                if (response?.data) {
                    setDevelopers(response.data);
                } else {
                    setDevelopers([]);
                }
            } catch (error) {
                console.error("Error fetching developers:", error);
                message.error("Failed to load developers");
                setDevelopers([]);
            } finally {
                setLoading(false);
            }
        };

        const fetchReporters = async () => {
            try {
                const response: any = await getallReporterLoginUser("");
                if (response) {
                    setReporters(response?.data);
                } else {
                    setReporters([]);
                }
            } catch (error) {
                console.error("Error fetching reporters:", error);
                message.error("Failed to load reporters");
                setReporters([]);
            }
        };
        const fetchProjectNames = async () => {
            try {
                const response: any = await getProjectName();
                setProjectName(response.data);
            } catch {
                message.error("Failed to load reporters");
                ProjectName([]);
            }
        };
        fetchProjectNames()
        fetchDevelopers();
        fetchReporters();
    }, []);

    // Submit handler
    const handleSubmit = async (values: any) => {
        try {
            setIsLoading(true);

            const formData = new FormData();

            const simpleFields = [
                "userId",
                "boardId",
                "taskName",
                "status",
                "priority",
                "reporterId",
            ];

            simpleFields.forEach((field) => {
                if (values[field] !== undefined && values[field] !== null) {
                    formData.append(field, values[field]);
                }
            });

            const htmlContent = editor?.getHTML().trim() || "";
            const plainText = editor?.getText().trim() || "";
            const hasImages = /<img\b/i.test(htmlContent);

            if (!plainText && !hasImages) {
                message.error("Please enter a task description or upload an image");
                setIsLoading(false);
                return;
            }

            formData.append("taskDesc", htmlContent);

            uploadedImages.forEach((file: File) => {
                formData.append("photo", file);
            });

            if (values.startDate) {
                formData.append("startDate", values.startDate.format());
            }
            if (values.deadlineDate) {
                formData.append("deadlineDate", values.deadlineDate.format());
            }

            formData.append("created", new Date().toISOString());

            (values.assignees || []).forEach((assigneeId: string) => {
                formData.append("assignees[]", assigneeId);
            });

            (values.subTasks || []).forEach((subtask: any, index: number) => {
                formData.append(`subTasks[${index}][subTaskName]`, subtask.subTaskName);
                formData.append(`subTasks[${index}][subTaskDesc]`, subtask.subTaskDesc);
                formData.append(`subTasks[${index}][status]`, subtask.status);

                (subtask.assignees || []).forEach((a: string, i: number) => {
                    formData.append(`subTasks[${index}][assignees][${i}]`, a);
                });

                formData.append(`subTasks[${index}][isActive]`, "true");
            });

            const response = await addTask(formData);
            console.log("Task creation response:", response);



            if (response && (response.status === 201 || response.status === 200)) {
                message.success("Task created successfully!");
                setDescValue("");
                setUploadedImages([]);
                form.resetFields();

                if (boards.length > 0) {
                    form.setFieldsValue({
                        boardId: boards.find((board: any) => board.slug === "to-do")?._id,
                    });
                }

                onTaskCreated();
            } else {
                message.error(response?.data?.message || "Failed to create task");
            }
        } catch (error: any) {
            message.error(
                "Task creation failed: " + (error.response?.data?.message || "Server error")
            );
        } finally {
            setIsLoading(false);
        }
    };


    const handleFormChange = (_: any, allValues: any) => {
        const { startDate, deadlineDate } = allValues;
        if (startDate && deadlineDate) {
            setIsStatusDisabled(false);
        } else {
            setIsStatusDisabled(true);
        }
    };

    useEffect(() => {
        if (boards.length > 0) {
            form.setFieldsValue({
                boardId: boards?.find((board: any) =>
                    board.slug === "to-do"
                )?._id
            });
        }
    }, [boards]);

    useEffect(() => {
        if (visible) {
            setDescValue("");
            editor?.commands.setContent("");
            form.resetFields();
            setUploadedImages([]);


        }
    }, [visible, form, editor]);


    useEffect(() => {
        if (!editor) return;

        const handleDrop = (event: DragEvent) => {
            event.preventDefault();

            const files = Array.from(event.dataTransfer?.files || []);
            files.forEach((file) => {
                if (!file.type.startsWith("image/")) return;

                const reader = new FileReader();
                reader.onload = () => {
                    //@ts-ignore
                    editor.chain().focus().setImage({ src: reader.result as string, width: "200px" }).run();
                    setUploadedImages((prev) => [...prev, file]);
                };
                reader.readAsDataURL(file);
            });
        };

        const handlePaste = (event: ClipboardEvent) => {
            const items = Array.from(event.clipboardData?.items || []);
            items.forEach((item) => {
                if (item.type.indexOf("image") === -1) return;
                const file = item.getAsFile();
                if (!file) return;

                const reader = new FileReader();
                reader.onload = () => {
                    //@ts-ignore
                    editor.chain().focus().setImage({ src: reader.result as string, width: "200px" }).run();
                    setUploadedImages((prev) => [...prev, file]);
                };
                reader.readAsDataURL(file);
            });
        };

        const editorElement = editor.view.dom;
        editorElement.addEventListener("drop", handleDrop);
        editorElement.addEventListener("paste", handlePaste);

        return () => {
            editorElement.removeEventListener("drop", handleDrop);
            editorElement.removeEventListener("paste", handlePaste);
        };
    }, [editor]);

    const handleCancel = () => {
        onClose(); // Use the prop function instead
    };

    const handleDeletePhoto = () => {
        setFileList([]);
    };
    return (
        <>
            <Modal
                open={visible}
                onCancel={onClose}
                footer={null}
                width={700}
                centered
                className="custom-modal"
                bodyStyle={{
                    overflowY: "auto",
                    fontFamily: "inter, sans-serif",
                }}
                styles={{
                    header: {
                        padding: "12px",
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f1f5ffff",
                    },
                    content: {
                        padding: 0,
                        overflow: "hidden",
                    },
                }}
                title={
                    <div
                        style={{
                            color: "#4b5563",
                            fontFamily: "inter, sans-serif",
                            fontSize: "20px",
                            fontWeight: 500,
                        }}
                    >
                        Create New Task
                    </div>
                }
            >
                <Form
                    onFinish={handleSubmit}
                    form={form}
                    layout="horizontal"
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 19 }}
                    colon={false}

                >
                    <Form.Item
                        label={<span style={{
                            color: '#4b5563', // Medium gray color similar to the image
                            fontFamily: 'inter, sans-serif', // Matches the clean sans-serif style
                            fontSize: '14px', // Approximate font size based on the image
                            fontWeight: 500, // Medium weight to match "font-medium"
                            paddingLeft: "15px",

                        }}>Board<span className="text-red-500">*</span></span>}
                        required={false}
                        name="boardId"
                        rules={[{ required: true, message: "Please select a board" }]}
                    >
                        <Select placeholder="Select Board" disabled={!isAdmin}>
                            {boards.map((board: any) => (
                                <Option key={board._id} value={board._id}>
                                    {board.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {/* <Form.Item
                        label={<span style={{
                            color: '#4b5563', // Medium gray color similar to the image
                            fontFamily: 'inter, sans-serif', // Matches the clean sans-serif style
                            fontSize: '14px', // Approximate font size based on the image
                            fontWeight: 500, // Medium weight to match "font-medium"
                            paddingLeft: "15px",

                        }}>Task Name<span className="text-red-500">*</span></span>}
                        required={false}
                        name="taskName"
                        rules={[{ required: true, message: "Please enter task name" }]}
                    >
                        <Input placeholder="Enter task name" className="Allinput" />
                    </Form.Item> */}

                    <Form.Item
                        label={<span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            fontWeight: 500,
                            paddingLeft: "15px",
                        }}>Task Name<span className="text-red-500">*</span></span>}
                        required={false}
                        name="taskName"
                        rules={[{ required: true, message: "Please enter task name" }]}
                    >
                        <Select placeholder="Select Project" showSearch>
                            {ProjectName.map((p: any) => (
                                <Option key={p._id} value={p.projectName}>
                                    {p.projectName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={<span style={{
                            color: '#4b5563', // Medium gray color similar to the image
                            fontFamily: 'inter, sans-serif', // Matches the clean sans-serif style
                            fontSize: '14px', // Approximate font size based on the image
                            fontWeight: 500, // Medium weight to match "font-medium"
                            paddingLeft: "15px",

                        }}>Description<span className="text-red-500">*</span></span>}
                        rules={[{ required: true, message: "Please enter description" }]}

                    >

                        <div className="border rounded bg-white p-2">
                            <div className="flex gap-2">
                                <EditorContent
                                    editor={editor}
                                    className="flex-1 min-h-[100px]  outline-none p-2"
                                />
                                <Button
                                    onClick={addImage}
                                    type="default"
                                    icon={<UploadOutlined />}
                                    style={{ border: "none", boxShadow: "none", alignSelf: "flex-start" }}
                                />
                            </div>
                        </div>
                    </Form.Item>



                     <Form.Item
                        label={<span style={{
                            color: '#4b5563', // Medium gray color similar to the image
                            fontFamily: 'inter, sans-serif', // Matches the clean sans-serif style
                            fontSize: '14px', // Approximate font size based on the image
                            fontWeight: 500, // Medium weight to match "font-medium"
                            paddingLeft: "15px",

                        }}>Deadline Date<span className="text-red-500">*</span></span>}
                        required={false}
                        name="deadlineDate"
                        rules={[{ required: true, message: "Please select deadline" }]}
                    >
                       <DatePicker
                                            showTime
                                            format="DD-MM-YYYY HH:mm"
                                            className="w-full"
                                            open={datePickerOpen}
                                            onOpenChange={(open) => {
                                              setDatePickerOpen(open);
                                              // When closing without selection, ensure we have the current value
                                              if (!open) {
                                                const currentValue = form.getFieldValue('deadlineDate');
                                                if (currentValue) {
                                                  handleDeadlineDateChange(currentValue);
                                                }
                                              }
                                            }}
                                            onChange={(date) => {
                                              // Auto-save when date changes (including time changes)
                                              handleDeadlineDateChange(date);
                                            }}
                                            //@ts-ignore
                                            onSelect={(date) => {
                                              // Immediate save when selecting from calendar
                                              handleDeadlineDateChange(date);
                                            }}
                                            onOk={() => {
                                              // Empty function - we don't need OK button
                                            }}
                                            // Remove the need for confirmation
                                            needConfirm={false}
                                            disabledDate={(current) => {
                                              return current && current < dayjs().startOf('day');
                                            }}
                                            showNow={true}
                                            // Optional: Auto-close when time is selected
                                            onPanelChange={(value, mode) => {
                                              if (mode === 'time') {
                                                // When switching to time panel, you might want different behavior
                                              }
                                            }}
                                          />
                                        </Form.Item>

                    <Form.Item
                        label={<span style={{
                            color: '#4b5563', // Medium gray color similar to the image
                            fontFamily: 'inter, sans-serif', // Matches the clean sans-serif style
                            fontSize: '14px', // Approximate font size based on the image
                            fontWeight: 500, // Medium weight to match "font-medium"
                            paddingLeft: "15px",

                        }}>Priority<span className="text-red-500">*</span></span>}
                        required={false}
                        name="priority"
                        rules={[{ required: true, message: "Please select priority" }]}
                    >
                        <Select placeholder="Select Priority">
                            <Option value="Low">Low</Option>
                            <Option value="Medium">Medium</Option>
                            <Option value="High">High</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={<span style={{
                            color: '#4b5563', // Medium gray color similar to the image
                            fontFamily: 'inter, sans-serif', // Matches the clean sans-serif style
                            fontSize: '14px', // Approximate font size based on the image
                            fontWeight: 500, // Medium weight to match "font-medium"
                            paddingLeft: "15px",

                        }}>Assignees<span className="text-red-500">*</span></span>}
                        required={false}
                        name="assignees"
                        rules={[{ required: true, message: "Please select at least one assignee" }]}
                    >
                        <Select mode="multiple" placeholder="Select Assignees" filterOption={(input, option) =>
                            (option?.children ?? "")
                                .toString()
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }>
                            {developers.map((dev) => (
                                <Option key={dev._id} value={dev._id}>
                                    {dev.username}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label={<span style={{
                        color: '#4b5563', // Medium gray color similar to the image
                        fontFamily: 'inter, sans-serif', // Matches the clean sans-serif style
                        fontSize: '14px', // Approximate font size based on the image
                        fontWeight: 500, // Medium weight to match "font-medium"
                        paddingLeft: "15px",

                    }}>Reporter<span className="text-red-500"></span></span>}
                        required={false} name="reporterId">
                        <Select placeholder="Select Reporter">
                            {reporters.map((r) => (
                                <Option key={r._id} value={r._id}>
                                    {r.reporterName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label={<span style={{
                        color: '#4b5563', // Medium gray color similar to the image
                        fontFamily: 'inter, sans-serif', // Matches the clean sans-serif style
                        fontSize: '14px', // Approximate font size based on the image
                        fontWeight: 500, // Medium weight to match "font-medium"
                        paddingLeft: "15px",

                    }}>SubTasks<span className="text-red-500">*</span></span>}
                        required={false} wrapperCol={{ span: 18 }}>
                        <Form.List name="subTasks">
                            {(fields, { add, remove }) => (
                                <div>
                                    <Button
                                        type="dashed"
                                        icon={<PlusOutlined />}
                                        className="mb-3"
                                        onClick={() => add()}
                                    >
                                        Add Subtask
                                    </Button>
                                    {fields.map(({ key, name, ...restField }) => (
                                        <div
                                            key={key}
                                            className="border rounded p-3 mb-3 bg-white shadow-sm"
                                        >
                                            <div className="grid grid-cols-2 gap-3">
                                                <Form.Item {...restField} name={[name, "subTaskName"]}>
                                                    <Input placeholder="Subtask name" className="Allinput " />
                                                </Form.Item>
                                                <Form.Item {...restField} name={[name, "subTaskDesc"]}>
                                                    <Input placeholder="Description" className="Allinput" />
                                                </Form.Item>
                                                <Form.Item {...restField} name={[name, "status"]}>
                                                    <Select placeholder="Status">
                                                        <Option value="Pending">Pending</Option>
                                                        <Option value="In Progress">In Progress</Option>
                                                        <Option value="Completed">Completed</Option>
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item {...restField} name={[name, "assignees"]}>
                                                    <Select mode="multiple" placeholder="Assignees">
                                                        {developers.map((dev) => (
                                                            <Option key={dev._id} value={dev._id}>
                                                                {dev.username}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </div>
                                            <Button
                                                danger
                                                type="text"
                                                onClick={() => remove(name)}
                                            >
                                                Remove
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Form.List>
                    </Form.Item>

                    <div className="flex justify-end gap-3 pr-5">
                        <button type="button" className="allcanclebutton" onClick={onClose} disabled={isLoading}>
                            Cancel
                        </button>
                        <button className="allsubmitebutton" type="submit">
                            {isLoading ? "Add..." : "Add"}
                        </button>
                    </div>
                </Form>
            </Modal >
        </>
    );
};

export default TaskFormModal;

// task modal new 
